import { atom } from 'jotai';
import { atomWithStorage, RESET } from 'jotai/utils';
import { isNil, omit } from 'ramda';
import { registerSharedAtom } from './jotai-utils';

const lastSavedKey = 'lastSavedKey';

/**
 * Factory that builds a storedAtom helper bound to a Storage-like object.
 * All atoms of a factory are persisted together as one JSON object under `storageKey`, so this
 * plugs a custom storage into atomWithStorage instead of using one storage entry per atom.
 * @param {Storage} storage - sessionStorage or localStorage
 */
const createStoredAtomFactory = storage => (storageKey, persistenceTime) => {
  // Unparseable storage is treated as empty so the next write replaces it instead of throwing on every render.
  const readAll = () => {
    try {
      const savedValue = storage.getItem(storageKey);
      return savedValue ? JSON.parse(savedValue) : {};
    } catch (e) {
      return {};
    }
  };
  const writeAll = values => storage.setItem(storageKey, JSON.stringify({ ...values, [lastSavedKey]: new Date() }));

  const groupStorage = {
    getItem: (key, initialValue) => {
      const parsed = readAll();
      const lastTime = parsed[lastSavedKey];
      if (persistenceTime && lastTime && Date.now() - new Date(lastTime).valueOf() > persistenceTime) {
        storage.removeItem(storageKey);
        return initialValue;
      }
      return isNil(parsed[key]) ? initialValue : parsed[key];
    },
    setItem: (key, value) => writeAll({ ...readAll(), [key]: value }),
    removeItem: key => writeAll(omit([key], readAll()))
  };

  /**
   * @param {string} key - key of the value inside the stored object, also identifies the atom across realms
   * @param {*} default - value used when nothing (or an expired entry) is stored, and on reset
   * @param {function} [normalize] - maps every new value before it is stored and persisted
   */
  const storedAtom = ({ key, default: defaultValue, normalize }) =>
    registerSharedAtom(key, () => {
      const storageAtom = atomWithStorage(key, defaultValue, groupStorage, { getOnInit: true });
      if (!normalize) return storageAtom;
      return atom(
        get => get(storageAtom),
        (get, set, update) => {
          if (update === RESET) return set(storageAtom, RESET);
          const nextValue = typeof update === 'function' ? update(get(storageAtom)) : update;
          return set(storageAtom, normalize(nextValue));
        }
      );
    });

  return { storedAtom };
};

export const storedAtomFactory = createStoredAtomFactory(sessionStorage);
export const persistentStoredAtomFactory = createStoredAtomFactory(localStorage);
